import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import React, { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { InviteAdminFormValues } from '../../../library/models/clinic';
import ResourcesIds from '../../../resources/ResourcesIds';
import { createInviteAdminSchema } from './inviteAdminSchema';
import { useIsCompact } from '../../../library/useIsCompact';
import { descriptionStyles, dialogActionsStyles, dialogFormStyles, dialogTitleStyles, fieldsBoxStyles, formAlertStyles, namesBoxStyles } from './inviteAdminDialog.styles';

interface InviteAdminDialogProps {
    isOpen: boolean;
    clinicName: string;
    onClose: () => void;
    /** Rejects with the API error, which this dialog maps back onto the fields. */
    onSubmit: (values: InviteAdminFormValues) => Promise<unknown>;
}

const DEFAULT_VALUES: InviteAdminFormValues = {
    emailAddress: '',
    firstName: '',
    lastName: '',
};

/**
 * Appoints a clinic's administrator. The name fields are optional because the invitee
 * fills in whatever is missing when they claim the account -- the address is the only
 * thing that has to be right, since it is where the invitation goes.
 *
 * Uses no router hooks, so it renders on its own in a test.
 */
export const InviteAdminDialog = (props: InviteAdminDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    const schema = React.useMemo(() => createInviteAdminSchema(translate), [translate]);

    const {
        register, handleSubmit, reset, setError, formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<InviteAdminFormValues>(schema, { defaultValues: DEFAULT_VALUES });

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<InviteAdminFormValues>(
        setError, knownFields, translate, ResourcesIds.INVITE_ADMIN_ERROR);

    // Cleared on every open, so a previous invitation's address is never resent by accident.
    useEffect(() => {
        if (props.isOpen) {
            reset(DEFAULT_VALUES);
            clear();
        }
    }, [props.isOpen, reset, clear]);

    const onValid = async (values: InviteAdminFormValues): Promise<void> => {
        clear();

        try {
            await props.onSubmit(values);
        } catch (error: unknown) {
            report(error);
        }
    };

    return (
        <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.INVITE_ADMIN_TITLE, { clinicName: props.clinicName })}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.INVITE_ADMIN_DESCRIPTION)}
                    </Typography>

                    <Box sx={fieldsBoxStyles}>
                        <TextField
                            {...register('emailAddress')}
                            fullWidth
                            autoFocus
                            type="email"
                            label={translate(ResourcesIds.EMAIL_LABEL)}
                            {...fieldProps(errors, 'emailAddress')}
                        />

                        <Box sx={namesBoxStyles}>
                            <TextField
                                {...register('firstName')}
                                fullWidth
                                label={translate(ResourcesIds.FIRST_NAME_LABEL)}
                                {...fieldProps(errors, 'firstName')}
                            />

                            <TextField
                                {...register('lastName')}
                                fullWidth
                                label={translate(ResourcesIds.LAST_NAME_LABEL)}
                                {...fieldProps(errors, 'lastName')}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    <Button onClick={props.onClose} variant="outlined" disabled={isSubmitting}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                        {translate(ResourcesIds.INVITE_ADMIN_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
